import React from 'react';
import Typography from '@mui/material/Typography';
import { Text, useTranslator } from '@eo-locale/react';
import type { TripadvisorDto } from '@checkfront/guest-experience-api-api-client-javascript-axios';
import { classes } from '@guest-widgets/shared/src/classes';
import { Feature } from '@guest-widgets/shared/src/features';
import { useFeature } from '@guest-widgets/shared/src/contexts/featureContext/featureContext';
import { calcCssSize } from '@guest-widgets/shared/src/utils/themeCalculator';
import { styled } from '@guest-widgets/core';

import { TripadvisorLogo } from './TripadvisorLogo';

export interface TripadvisorProps {
  data?: TripadvisorDto;
}

const Tripadvisor = ({ data }: TripadvisorProps) => {
  const { isEnabled } = useFeature();
  const { translate } = useTranslator();

  if (!isEnabled(Feature.Tripadvisor)) return null;
  if (!data || !data.webUrl) return null;

  return (
    <Container
      target="_blank"
      rel="noopener"
      href={data.webUrl}
      className={classes.details.tripadvisor}
    >
      <div>
        <TripadvisorLogoStyled />
        <Description variant="caption">
          <Text id="product-details.tripadvisor.description" />
        </Description>
      </div>
      <Footer>
        <Title>
          <Text id="product-details.tripadvisor.title" />
        </Title>
        <Reviews>
          <ImageContainer>
            <img src={data.ratingImageUrl} alt={translate('product-details.tripadvisor.title')} />
          </ImageContainer>
          <Text id="product-details.tripadvisor.reviews" length={data.reviewsCount} />
        </Reviews>
      </Footer>
    </Container>
  );
};

const ImageContainer = styled('div')(({ theme: { spacing, shape, palette } }) => ({
  backgroundColor: palette.common.white,
  margin: spacing(0, 1),
  borderRadius: shape.borderRadius,
  display: 'flex',
}));

const Title = styled((props) => <Typography variant="h4" {...props} />)(
  ({ theme: { typography }, widgetArea }) => ({
    fontSize: widgetArea.basedOnSize(
      typography.h4.fontSize,
      typography.h4.fontSize,
      calcCssSize(typography.fontSize, 1.5)
    ),
  })
);

const Description = styled(Typography)(({ theme: { palette } }) => ({
  color: palette.text.primary,
  display: 'block',
}));

const TripadvisorLogoStyled = styled(TripadvisorLogo)(
  ({ theme: { palette, spacing, typography } }) => ({
    fontSize: calcCssSize(typography.fontSize, 3.28),
    width: 'auto',
    marginBottom: spacing(3),
    color: palette.text.primary,
  })
);

const Reviews = styled('div')(({ theme: { palette, spacing } }) => ({
  display: 'flex',
  marginTop: spacing(4),
  marginLeft: spacing(-2),
  color: palette.text.primary,
}));

const Container = styled('a')(({ theme: { spacing, border, shape, palette } }) => ({
  marginTop: spacing(8),
  padding: spacing(5),
  border: border,
  borderRadius: shape.borderRadius,
  textDecoration: 'none',
  display: 'block',
  color: palette.text.primary,
}));

const Footer = styled('div')(({ theme: { spacing, border } }) => ({
  marginTop: spacing(3),
  padding: `${spacing(3)} 0`,
  borderTop: border,
}));

export default Tripadvisor;
